import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Brain,
  BookOpen,
  Target,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const AREA_ICONS = {
  pensamiento_matematico: Brain,
  comprension_lectora: BookOpen,
  redaccion_indirecta: Target
};

const AREA_NAMES = {
  pensamiento_matematico: "Pensamiento Matemático",
  comprension_lectora: "Comprensión Lectora",
  redaccion_indirecta: "Redacción Indirecta"
};

export default function DiagnosticExam() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(45 * 60); // 45 minutes in seconds
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [examStarted, setExamStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);

  // Fetch questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API}/diagnostic-exam`);
        setQuestions(response.data.questions);
        setTimeRemaining(response.data.total_time_minutes * 60);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching exam:", error);
        toast.error("Error al cargar el examen");
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  // Timer
  useEffect(() => {
    if (!examStarted || !timerEnabled || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, timerEnabled, timeRemaining]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);

    const timeTaken = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    
    const submission = {
      answers: Object.entries(answers).map(([questionId, selectedOptionId]) => ({
        question_id: questionId,
        selected_option_id: selectedOptionId
      })),
      time_taken_seconds: timeTaken
    };

    try {
      const response = await axios.post(`${API}/diagnostic-exam/submit`, submission);
      navigate("/resultados", { state: { result: response.data } });
    } catch (error) {
      console.error("Error submitting exam:", error);
      toast.error("Error al enviar el examen");
      setSubmitting(false);
    }
  }, [answers, startTime, submitting, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const startExam = () => {
    setExamStarted(true);
    setStartTime(Date.now());
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Cargando examen...</p>
        </div>
      </div>
    );
  }

  if (!examStarted) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <a href="/" data-testid="logo-link" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-purple-100 shadow-lg" data-testid="exam-intro-card">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-purple-600" />
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Examen Diagnóstico EXANI II
              </h1>

              <p className="text-slate-600 mb-6">
                Este examen evalúa tu nivel actual en las tres áreas principales del EXANI II.
                Al finalizar, recibirás tu resultado con recomendaciones personalizadas.
              </p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">{questions.length}</p>
                  <p className="text-sm text-slate-500">Preguntas</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">45</p>
                  <p className="text-sm text-slate-500">Minutos</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">3</p>
                  <p className="text-sm text-slate-500">Áreas</p>
                </div>
              </div>

              <div className="bg-purple-50 rounded-xl p-4 mb-8 text-left">
                <h3 className="font-semibold text-slate-900 mb-2">Áreas del examen:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-600" />
                    Pensamiento Matemático
                  </li>
                  <li className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                    Comprensión Lectora
                  </li>
                  <li className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-600" />
                    Redacción Indirecta
                  </li>
                </ul>
              </div>

              <div className="flex items-center justify-center gap-3 mb-6">
                <input
                  type="checkbox"
                  id="timer-toggle"
                  checked={timerEnabled}
                  onChange={(e) => setTimerEnabled(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                  data-testid="timer-toggle"
                />
                <label htmlFor="timer-toggle" className="text-sm text-slate-600">
                  Activar temporizador (45 minutos)
                </label>
              </div>

              <Button
                onClick={startExam}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 px-10 text-base font-medium"
                data-testid="start-exam-btn"
              >
                Comenzar Examen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <a href="/" data-testid="logo-link" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-8 md:h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>

            {/* Timer */}
            {timerEnabled && (
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  timeRemaining < 300 ? "bg-red-100 text-red-700" : "bg-purple-100 text-purple-700"
                }`}
                data-testid="exam-timer"
              >
                <Clock className={`w-5 h-5 ${timeRemaining < 300 ? "animate-pulse" : ""}`} />
                <span className="font-mono font-semibold">{formatTime(timeRemaining)}</span>
              </div>
            )}

            <div className="text-sm text-slate-500">
              <span className="font-medium text-purple-600">{answeredCount}</span> / {questions.length} respondidas
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Question Area */}
          <div className="md:col-span-2">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                <span>Pregunta {currentIndex + 1} de {questions.length}</span>
                <span>{Math.round(progress)}% completado</span>
              </div>
              <Progress value={progress} className="h-2" data-testid="exam-progress" />
            </div>

            {/* Question Card */}
            {currentQuestion && (
              <Card className="border-slate-200 shadow-sm" data-testid="question-card">
                <CardContent className="p-6 md:p-8">
                  {/* Area Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    {(() => {
                      const Icon = AREA_ICONS[currentQuestion.area] || Brain;
                      return <Icon className="w-4 h-4 text-purple-600" />;
                    })()}
                    <span className="text-sm font-medium text-purple-600">
                      {AREA_NAMES[currentQuestion.area] || currentQuestion.area}
                    </span>
                  </div>

                  {/* Question Text */}
                  <h2 className="text-lg md:text-xl font-medium text-slate-900 mb-6">
                    {currentQuestion.text}
                  </h2>

                  {/* Options */}
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={(value) =>
                      setAnswers({ ...answers, [currentQuestion.id]: value })
                    }
                    className="space-y-3"
                    data-testid="question-options"
                  >
                    {currentQuestion.options.map((option, index) => (
                      <div key={option.id}>
                        <Label
                          htmlFor={option.id}
                          className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                            answers[currentQuestion.id] === option.id
                              ? "border-purple-500 bg-purple-50"
                              : "border-slate-200 hover:border-purple-200 hover:bg-slate-50"
                          }`}
                          data-testid={`option-${index}`}
                        >
                          <RadioGroupItem
                            value={option.id}
                            id={option.id}
                            className="text-purple-600"
                          />
                          <span className="text-slate-700">{option.text}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <Button
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                variant="outline"
                className="rounded-full"
                data-testid="prev-question-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>

              {currentIndex < questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentIndex(currentIndex + 1)}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                  data-testid="next-question-btn"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                  data-testid="submit-exam-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finalizar Examen
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar - Question Navigator */}
          <div className="hidden md:block">
            <Card className="border-slate-200 sticky top-24">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Navegación</h3>
                <div className="grid grid-cols-5 gap-2">
                  {questions.map((q, index) => (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                        index === currentIndex
                          ? "bg-purple-600 text-white"
                          : answers[q.id]
                          ? "bg-teal-100 text-teal-700"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`nav-question-${index}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <div className="w-4 h-4 rounded bg-teal-100"></div>
                    <span>Respondida</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <div className="w-4 h-4 rounded bg-slate-100"></div>
                    <span>Sin responder</span>
                  </div>
                </div>

                {answeredCount < questions.length && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                      <p className="text-xs text-yellow-700">
                        Tienes {questions.length - answeredCount} pregunta(s) sin responder
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                  data-testid="sidebar-submit-btn"
                >
                  {submitting ? "Enviando..." : "Finalizar Examen"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
